import type { ComponentProps } from "react";
import { createContext, useContext } from "react";

import { useMediaQuery } from "@/shared/hooks";
import { DialogPrimitive } from "./dialog-primitive";
import { DrawerPrimitive as Drawer } from "./drawer-primitive";

const DialogContext = createContext<boolean>(false);

const useDialogContext = () => {
  const context = useContext(DialogContext);

  if (context === undefined) {
    throw new Error("Dialog Components must be used within <Dialog.Root>");
  }
  return context;
};

type RootProps = ComponentProps<typeof DialogPrimitive.Root> & {
  alert?: boolean;
};

const Root = ({ alert = false, ...props }: RootProps) => {
  const isMobile = useMediaQuery();

  const Component = isMobile ? Drawer.Root : DialogPrimitive.Root;

  return (
    <DialogContext.Provider value={isMobile}>
      <Component dismissible={!alert} {...props} />
    </DialogContext.Provider>
  );
};

const Trigger = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Trigger : DialogPrimitive.Trigger;

  return <Component {...props} />;
};

const Content = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Content : DialogPrimitive.Content;

  return <Component {...props} />;
};

const Header = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Header>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Header : DialogPrimitive.Header;

  return <Component {...props} />;
};

const Title = ({ ...props }: ComponentProps<typeof DialogPrimitive.Title>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Title : DialogPrimitive.Title;

  return <Component {...props} />;
};

const Description = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Description : DialogPrimitive.Description;

  return <Component {...props} />;
};

const Close = ({ ...props }: ComponentProps<typeof DialogPrimitive.Close>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Close : DialogPrimitive.Close;

  return <Component {...props} />;
};

const Footer = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Footer>) => {
  const isMobile = useDialogContext();

  const Component = isMobile ? Drawer.Footer : DialogPrimitive.Footer;

  return <Component {...props} />;
};

export const Dialog = {
  Root,
  Trigger,
  Content,
  Header,
  Title,
  Description,
  Close,
  Footer,
};
